import type { ReactNode } from 'react';
import { AlertsBell } from './AlertsBell.js';
import type { StalenessLevel, SyncStatus, UnattributedBooks } from '../api/types.js';
import { color, font, fontSize, radius } from '../theme/tokens.js';
import { useTheme } from '../theme/useTheme.js';

/**
 * The sticky header (FR-019, FR-015, FR-016, FR-065, R-17).
 *
 * Carries page context, the as-of date, sync status, and — where a page asks for it — the
 * account-search entry. It is also where the escalating staleness warning lives.
 *
 * **The search control is opt-in per page, via `onOpenSearch`.** Only Region Detail passes it
 * (owner decision 2026-08-28, narrowing FR-019): a result navigates to a Region Detail with the
 * client row already selected (FR-050), so on every other page the control offered a jump
 * straight out of the page the reader had just opened. FR-049 — `⌘K` from anywhere — is
 * unaffected, because `<CommandPalette>` owns that key listener; this button is a second, visible
 * way in, not the only one. Restoring it everywhere means passing the prop again from
 * `app/Routes.tsx`, not changing anything here.
 *
 * FR-065's escalation, and what it deliberately does not do:
 *
 * | Level      | When                                     | Treatment                          |
 * | ---------- | ---------------------------------------- | ---------------------------------- |
 * | `fresh`    | last scheduled refresh succeeded          | as-of date, quiet                  |
 * | `stale`    | last refresh failed or was skipped        | as-of date + quiet indicator       |
 * | `critical` | snapshot older than 72 hours              | **plus** a prominent, persistent banner naming the age |
 *
 * **The data keeps rendering at every level.** There is no age at which the dashboard blanks —
 * an implementation that hid the figures past 72 hours would look safer and would fail FR-065.
 * The level is computed server-side (R-17) so "72 hours" has one definition and a client with
 * a skewed clock cannot raise a false alarm or suppress a real one.
 */

export interface HeaderProps {
  crumb: string;
  title: string;
  asOf: string | null;
  /**
   * When the daily job last promoted a snapshot (`Envelope.syncedAt`), ISO-8601 UTC.
   *
   * Optional, and omitted on Settings for the same reason `asOf` is null there: that page
   * carries directory state, not snapshot data, so there is no refresh for a time to describe.
   */
  syncedAt?: string | null;
  syncStatus: SyncStatus;
  staleness: { ageHours: number; level: StalenessLevel } | null;
  unattributedBooks?: UnattributedBooks | null;
  /** Omitted on every page but Region Detail, which hides the Search Account control. */
  onOpenSearch?: () => void;
  actions?: ReactNode;
  /**
   * A page whose figures are NOT the daily snapshot's names its own clock instead (constitution
   * 3.0.0 gate 7). Only the Audit page does (spec 007, FR-AU28): its figures describe an audit
   * RUN, so printing `Date as of <snapshot>` above them would be the two-clocks trap (FR-015a)
   * arriving on a new page. When set, it replaces the date, the time and the health word — the
   * snapshot's FR-065 critical banner below is product-wide and is left alone.
   */
  clock?: {
    label: string;
    date: string | null;
    /** Shown only when the source provides it — never approximated by "now" (FR-AU29). */
    completedAt: string | null;
    stale: boolean;
    /**
     * The page's clock is still being fetched. With no date yet the face stays EMPTY rather than
     * reading "No data yet" — that is a claim about the source, and nothing has answered yet.
     */
    pending?: boolean;
  };
}

function formatAsOf(asOf: string | null): string {
  if (!asOf) return 'No data yet';
  const date = new Date(`${asOf}T00:00:00.000Z`);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The clock time of the last successful refresh, in the READER'S zone.
 *
 * Deliberately a different clock from the date beside it. `asOf` is a UTC business date
 * (FR-058) and has to stay one — it names the day the figures describe. "When did the job
 * run" is a wall-clock question about the reader's own morning, so it answers in their zone.
 *
 * Which means the two can disagree about the calendar day: the schedule is 06:00 UTC, so
 * anyone west of UTC-6 sees a run that landed the previous evening. When that happens the time
 * carries its own date instead of borrowing the one it sits next to — `Sep 10, 2026 · 11:00 PM`
 * for a run that happened at 11 PM on the 9th is a single timestamp that never existed, and
 * nothing else on screen would catch it.
 */
function formatSyncedAt(asOf: string | null, syncedAt: string | null | undefined): string | null {
  if (!syncedAt) return null;
  const at = new Date(syncedAt);
  if (Number.isNaN(at.getTime())) return null;

  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const pad = (n: number) => String(n).padStart(2, '0');
  const localDate = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  if (asOf && localDate !== asOf) {
    return `${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
  }
  return time;
}

/** The hover detail: both facts spelled out, with the zone the time is in. */
function syncTooltip(asOf: string | null, syncedAt: string | null | undefined): string {
  const asOfLine = `Data as of ${formatAsOf(asOf)}`;
  if (!syncedAt) return asOfLine;
  const at = new Date(syncedAt);
  if (Number.isNaN(at.getTime())) return asOfLine;
  // Spelled out component by component, NOT `dateStyle`/`timeStyle`: Intl rejects those
  // combined with `timeZoneName` and throws, which here would take the whole header down
  // rather than just the tooltip. `tests/header.syncedat.test.tsx` is what caught it.
  const full = at.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${asOfLine} · last synced ${full}`;
}

/**
 * The health word — and there ISN'T one when the snapshot is healthy (owner request 2026-09-11).
 *
 * `Live` used to sit under the date on every page, which is FR-065's `fresh` row spelled out:
 * a word that never changes states nothing, and a permanent indicator is exactly what makes the
 * changed one easy to miss. ⚠ **Only the healthy label went.** The three that report a problem —
 * `No sync yet`, `Stale — last sync failed`, `Stale — critical` — are FR-065 itself and must
 * stay, as must the critical banner below; removing them is a different change and not this one.
 */
function statusLabel(syncStatus: SyncStatus, level: StalenessLevel | undefined): string | null {
  if (syncStatus === 'no_data') return 'No sync yet';
  if (level === 'critical') return 'Stale — critical';
  if (syncStatus === 'degraded' || level === 'stale') return 'Stale — last sync failed';
  return null;
}

function statusColor(syncStatus: SyncStatus, level: StalenessLevel | undefined): string {
  if (level === 'critical') return color.negativeStrong;
  if (syncStatus === 'degraded' || level === 'stale') return color.warning;
  if (syncStatus === 'no_data') return color.inkSubtle;
  return color.nursing;
}

/**
 * Light / dark / follow-system, cycling in that order.
 *
 * A cycle rather than a switch because the preference has three states and one of them —
 * following the OS — is the default a reader should be able to get back to. A two-way switch
 * strands them on an explicit choice forever after one click.
 *
 * The glyph shows what is CURRENTLY on screen, and the label says which of the three is selected,
 * because on `system` those are different facts: "system (dark)" is one click from "light", and a
 * sun icon alone could not tell you whether the next click follows the OS or overrides it.
 */
function ThemeToggle() {
  const { choice, resolved, cycle } = useTheme();
  const next = choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system';

  return (
    <button
      type="button"
      onClick={cycle}
      className="chip"
      aria-label={`Theme: ${choice === 'system' ? `system (${resolved})` : choice}. Switch to ${next}.`}
      title={`Theme: ${choice === 'system' ? `system — currently ${resolved}` : choice}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        flex: '0 0 auto',
        background: color.surface,
        border: `1px solid ${color.borderStrong}`,
        borderRadius: radius.xl,
        color: color.inkMuted,
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {/* Sun and moon at 15px, matching the search control's icon weight. `currentColor`
          throughout, so the glyph follows the button's themed ink with no second palette. */}
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {resolved === 'dark' ? (
          <path
            d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M18.8 5.2l-1.6 1.6M6.8 17.2l-1.6 1.6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
      {/* The selected state, for assistive tech and nothing else — three words in the header
          would crowd the as-of date, which FR-015 requires to be the thing you notice. */}
      <span className="sr-only">
        {choice === 'system' ? `Following system, currently ${resolved}` : `${choice} mode`}
      </span>
    </button>
  );
}

export function Header({
  crumb,
  title,
  asOf,
  syncedAt,
  syncStatus,
  staleness,
  unattributedBooks,
  onOpenSearch,
  actions,
  clock,
}: HeaderProps) {
  const level = staleness?.level;
  const isCritical = level === 'critical';
  const faceDate = clock ? clock.date : asOf;
  const faceLabel = clock ? `${clock.label} ` : 'Date as of ';
  const syncTime = clock
    ? formatSyncedAt(clock.date, clock.completedAt)
    : formatSyncedAt(asOf, syncedAt);
  const faceTooltip = clock
    ? `${clock.label} ${formatAsOf(clock.date)}`
    : syncTooltip(asOf, syncedAt);
  const healthWord = clock
    ? clock.stale
      ? `Stale — latest audit run ${formatAsOf(clock.date)}`
      : ''
    : statusLabel(syncStatus, level);
  const healthColor = clock ? color.warning : statusColor(syncStatus, level);
  const hasDataQualityIssue = (unattributedBooks?.candidateCount ?? 0) > 0;

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: color.headerBg,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${color.borderStrong}`,
          padding: '16px 30px',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: fontSize.caption,
              fontWeight: 600,
              letterSpacing: '.5px',
              color: color.inkSubtle,
              textTransform: 'uppercase',
            }}
          >
            {crumb}
          </div>
          <h1
            style={{
              margin: '2px 0 0',
              fontSize: fontSize.pageTitle,
              fontWeight: 700,
              letterSpacing: '-.3px',
              color: color.ink,
            }}
          >
            {title}
          </h1>
        </div>

        {onOpenSearch ? (
          <button
            type="button"
            onClick={onOpenSearch}
            aria-label="Search accounts (Command K)"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: color.surface,
              border: `1px solid ${color.borderStrong}`,
              borderRadius: radius['2xl'],
              padding: '8px 12px',
              width: 250,
              cursor: 'pointer',
              color: color.inkFaint,
              fontSize: fontSize.control,
              fontFamily: 'inherit',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3-3" />
            </svg>
            <span style={{ flex: 1, textAlign: 'left' }}>Search Account</span>
            <span
              className="mono"
              style={{
                fontFamily: font.mono,
                fontSize: fontSize.label,
                fontWeight: 600,
                color: color.inkSubtle,
                background: color.surfaceChip,
                border: `1px solid ${color.borderChip}`,
                borderRadius: radius.sm,
                padding: '2px 6px',
              }}
            >
              ⌘K
            </span>
          </button>
        ) : null}

        {actions}

        {/* Every view carrying snapshot data shows the as-of date (FR-015, SC-004), and
            beside it the moment the daily job last landed. The time is the subordinate of the
            two — dimmer, lighter — because the date is what FR-015 requires you to notice and
            a refresh time competing with it for weight would bury the thing being labelled. */}
        <div style={{ textAlign: 'right', lineHeight: 1.2 }} title={faceTooltip}>
          <div
            className="mono"
            style={{
              fontFamily: font.mono,
              fontSize: fontSize.bodyStrong,
              fontWeight: 600,
              color: color.inkNumeric,
              whiteSpace: 'nowrap',
            }}
          >
            {/* The label the two values were missing (owner request 2026-09-11). The face used
                to be a bare date and time, which the tooltip had to explain; naming it on screen
                is what makes it readable without hovering. It is deliberately NOT mono and not
                `inkNumeric` — it is prose next to a figure, and matching the date's weight would
                make the label compete with the thing FR-015 requires you to notice. Omitted when
                there is no date, because "Date as of No data yet" is worse than the bare state. */}
            {faceDate ? (
              <span
                style={{
                  fontFamily: font.sans,
                  fontWeight: 500,
                  color: color.inkMuted,
                }}
              >
                {/* A real space, not a margin: the gap has to survive into the text content
                    a screen reader announces and a reader copies, where a margin does not. */}
                {faceLabel}
              </span>
            ) : null}
            {faceDate || !clock?.pending ? formatAsOf(faceDate) : null}
            {/* `inkMuted`, NOT `inkSubtle`. This is real text at body size and it has no
                blueprint precedent to defer to, so it meets AA rather than joining the seven
                documented shortfalls: measured on the light header, subtle is 2.73:1 and muted
                is 4.76:1. Dark passes either way, which is exactly why light is the one to
                check — see `tests/theme.contrast.test.ts`. */}
            {syncTime ? (
              <span style={{ fontWeight: 500, color: color.inkMuted }}>
                {' · '}
                {syncTime}
              </span>
            ) : null}
          </div>
          {/* The live region stays MOUNTED and empty while healthy, rather than being rendered
              only when there is something to say: a `role="status"` element inserted at the
              moment it fills is not reliably announced, so a snapshot going stale mid-session
              would change the header silently for a screen reader. */}
          <div style={{ fontSize: fontSize.label, color: healthColor }} role="status">
            {healthWord}
          </div>
        </div>

        {/* Spec 008: the Alerts bell, between the date block and the theme toggle (blueprint 127–130). */}
        <AlertsBell />
        <ThemeToggle />
      </header>

      {/* FR-065 — prominent, persistent, and naming the age. Past 72 hours an indicator that
          never changes is exactly the failure this prevents. */}
      {isCritical ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: color.warningBg,
            border: `1px solid ${color.negativeStrong}`,
            color: color.negativeStrong,
            borderRadius: radius['3xl'],
            padding: '12px 16px',
            margin: '16px 30px 0',
            fontSize: fontSize.bodyStrong,
            fontWeight: 500,
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span>
            This snapshot is <strong>{staleness!.ageHours} hours old</strong> — the scheduled
            refresh has not succeeded since {formatAsOf(asOf)}. Every figure below is from that
            snapshot and is still shown in full.
          </span>
        </div>
      ) : null}

      {/* FR-064 — the data-quality alarm rides the same channel as staleness (R-16). The
          per-figure indicator sits on BoB and its derived columns; this states the count. */}
      {hasDataQualityIssue ? (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: color.warningBg,
            border: `1px solid ${color.warningBorder}`,
            color: color.noticeInk,
            borderRadius: radius['3xl'],
            padding: '10px 16px',
            margin: '12px 30px 0',
            fontSize: fontSize.captionStrong,
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span>
            BoB and the candidate-status figures are <strong>under-reported</strong>:{' '}
            {unattributedBooks!.candidateCount} candidates across{' '}
            {unattributedBooks!.recruiterCount} recruiter
            {unattributedBooks!.recruiterCount === 1 ? '' : 's'} could not be resolved to a
            region in the last refresh.
          </span>
        </div>
      ) : null}
    </>
  );
}
